import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Shield, Plus, X, Save, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import api from '@/utils/api';
import { toast } from 'sonner';

const TORTLAB_LOGO = "https://customer-assets.emergentagent.com/job_35f2eb62-f2f6-4b48-9388-e2c1ee261a61/artifacts/tfwz2zen_generated-image-1%20%284%29.png";

export default function SystemSettings({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    global_disallowed_topics: [],
    classifier_strictness: 'moderate'
  });
  const [newTopic, setNewTopic] = useState('');

  useEffect(() => {
    // Redirect non-PlatformAdmin users
    if (user && user.role !== 'PlatformAdmin') {
      toast.error('Access denied. Platform Admin only.');
      navigate('/dashboard');
      return;
    }
    fetchSettings();
  }, [user, navigate]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/system-settings');
      if (response.data) {
        setSettings({
          global_disallowed_topics: response.data.global_disallowed_topics || [],
          classifier_strictness: response.data.classifier_strictness || 'moderate'
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // If no settings exist yet, use defaults
      if (error.response?.status === 404) {
        setSettings({
          global_disallowed_topics: [
            'weather forecasts',
            'sports scores and results',
            'current news and events',
            'political opinions',
            'general trivia questions',
            'math homework help',
            'coding and programming help',
            'medical advice',
            'legal advice',
            'financial investment advice'
          ],
          classifier_strictness: 'moderate'
        });
      } else {
        toast.error('Failed to load system settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddTopic = () => {
    if (!newTopic.trim()) return;
    if (settings.global_disallowed_topics.includes(newTopic.trim().toLowerCase())) {
      toast.error('This topic is already in the list');
      return;
    }
    setSettings({
      ...settings,
      global_disallowed_topics: [...settings.global_disallowed_topics, newTopic.trim().toLowerCase()]
    });
    setNewTopic('');
  };

  const handleRemoveTopic = (topicToRemove) => {
    setSettings({
      ...settings,
      global_disallowed_topics: settings.global_disallowed_topics.filter(t => t !== topicToRemove)
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTopic();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/system-settings', settings);
      toast.success('System settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="system-settings-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img src={TORTLAB_LOGO} alt="TortLab" className="h-8 object-contain" />
              <div>
                <h1 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-500" />
                  System Settings
                </h1>
                <p className="text-sm text-muted-foreground">Platform-wide configuration (Admin Only)</p>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving} className="rounded-full">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Warning Banner */}
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Platform Admin Only</p>
            <p className="text-sm text-amber-700">
              These settings apply to ALL agents across ALL tenants. Changes here will affect the behavior of every chat agent on the platform.
            </p>
          </div>
        </div>

        {/* Global Disallowed Topics Card */}
        <Card className="border-2 shadow-lg" data-testid="disallowed-topics-card">
          <CardHeader>
            <CardTitle className="text-xl font-heading flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-500" />
              Global Disallowed Topics
            </CardTitle>
            <CardDescription>
              Topics that ALL agents will refuse to discuss. The AI classifier will detect and block these topics system-wide.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Add New Topic */}
            <div className="flex gap-2">
              <Input
                placeholder="Add a disallowed topic (e.g., 'stock predictions')"
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1"
                data-testid="new-topic-input"
              />
              <Button onClick={handleAddTopic} variant="outline" data-testid="add-topic-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>

            {/* Topics List */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Current Disallowed Topics ({settings.global_disallowed_topics.length})</Label>
              <div className="flex flex-wrap gap-2 p-4 bg-slate-50 rounded-xl min-h-[100px]" data-testid="topics-list">
                {settings.global_disallowed_topics.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No topics added yet. Agents will rely on their individual business context for scope decisions.</p>
                ) : (
                  settings.global_disallowed_topics.map((topic, index) => (
                    <Badge 
                      key={index} 
                      variant="secondary" 
                      className="px-3 py-1.5 text-sm bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 group"
                      data-testid={`topic-badge-${index}`}
                    >
                      {topic}
                      <button
                        onClick={() => handleRemoveTopic(topic)}
                        className="ml-2 hover:text-red-900"
                        data-testid={`remove-topic-${index}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))
                )}
              </div>
            </div>

            {/* Bulk Add */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium">Bulk Add Topics</Label>
              <p className="text-xs text-muted-foreground mb-2">Paste multiple topics, one per line</p>
              <div className="flex gap-2">
                <Textarea
                  id="bulk-topics"
                  placeholder="weather forecasts&#10;sports scores&#10;political opinions"
                  className="min-h-[100px]"
                  data-testid="bulk-topics-input"
                />
                <Button 
                  variant="outline" 
                  onClick={() => {
                    const textarea = document.getElementById('bulk-topics');
                    const topics = textarea.value.split('\n').filter(t => t.trim());
                    const newTopics = topics.filter(t => !settings.global_disallowed_topics.includes(t.trim().toLowerCase()));
                    if (newTopics.length > 0) {
                      setSettings({
                        ...settings,
                        global_disallowed_topics: [...settings.global_disallowed_topics, ...newTopics.map(t => t.trim().toLowerCase())]
                      });
                      textarea.value = '';
                      toast.success(`Added ${newTopics.length} topics`);
                    } else {
                      toast.info('All topics already exist in the list');
                    }
                  }}
                  className="self-end"
                  data-testid="bulk-add-btn"
                >
                  Add All
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card className="mt-6 border-2">
          <CardHeader>
            <CardTitle className="text-lg font-heading">How the Classifier Works</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-3">
            <p>
              When a user sends a message, the AI classifier checks if the topic is relevant to the agent's business context.
            </p>
            <p>
              <strong>Global disallowed topics</strong> are checked first. If a message matches any topic in this list, 
              the agent will politely decline and redirect the user to ask about the business instead.
            </p>
            <p>
              This ensures consistent behavior across all agents while still allowing each agent to define their own 
              specific business context and expertise.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
